/**
 * File Type Utilities
 * Provides functions for determining file type icons, colours and labels based on file extensions
 * 
 * Supports common business file types including:
 * - Microsoft Office documents (Word, Excel, PowerPoint)
 * - Images (JPG, PNG, GIF, etc.)
 * - PDFs
 * - Archives (ZIP)
 * - Text and data files (TXT, CSV)
 * - Email files (MSG, EML)
 * 
 * For unknown file types, provides sensible defaults
 */

// Image file extensions
const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'tif', 'ico'];

// File type configuration map
const fileTypeConfig = {
  // Image files 
  ...Object.fromEntries(imageExtensions.map(ext => [ext, {
    icon: ['fas', 'file-image'],
    color: '#8e44ad', // purple
    label: 'Images',
  }])),
  // PDF files
  'pdf': {
    icon: ['fas', 'file-pdf'],
    color: '#dc3545', // red
    label: 'PDF Documents',
  },
  // Microsoft Office files
  'doc': {
    icon: ['fas', 'file-word'],
    color: '#2b579a', // Word blue
    label: 'Word Documents',
  },
  'docx': {
    icon: ['fas', 'file-word'],
    color: '#2b579a',
    label: 'Word Documents',
  },
  'xls': {
    icon: ['fas', 'file-excel'],
    color: '#217346', // Excel green
    label: 'Excel Spreadsheets',
  },
  'xlsx': {
    icon: ['fas', 'file-excel'],
    color: '#217346',
    label: 'Excel Spreadsheets',
  },
  'ppt': {
    icon: ['fas', 'file-powerpoint'],
    color: '#d24726', // PowerPoint orange-red
    label: 'PowerPoint Presentations',
  },
  'pptx': {
    icon: ['fas', 'file-powerpoint'],
    color: '#d24726',
    label: 'PowerPoint Presentations',
  },
  // Other document formats
  'txt': {
    icon: ['fas', 'file-alt'],
    color: '#6c757d',
    label: 'Text Files',
  },
  'csv': {
    icon: ['fas', 'file-csv'],
    color: '#28a745',
    label: 'CSV Files',
  },
  // Archived files
  'zip': {
    icon: ['fas', 'file-archive'],
    color: '#ffc107',
    label: 'ZIP Archives',
  },
  // Email files
  'msg': {
    icon: ['fas', 'envelope'],
    color: '#0078d4',
    label: 'Outlook Messages',
  },
  'eml': {
    icon: ['fas', 'envelope'],
    color: '#0078d4',
    label: 'Email Messages',
  },
};

/**
 * Get file extension from filename
 * @param {string} fileName - The name of the file
 * @returns {string|null} File extension in lowercase or null if no extension
 */
function getFileExtension(fileName) {
  if (!fileName) return null;
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : null;
}

/**
 * Get Font Awesome icon based on file extension
 * @param {string} fileName - The name of the file
 * @returns {Array} Font Awesome icon array [style, name] or ['fas', 'file'] for unknown types
 */
export function getFileTypeIcon(fileName) {
  const extension = getFileExtension(fileName);
  if (!extension) return ['fas', 'file'];
  // Check configured file types 
  const config = fileTypeConfig[extension];
  return config ? config.icon : ['fas', 'file'];
}

/**
 * Get the colour for file type icons
 * @param {string} fileName - The name of the file
 * @returns {Object} Style object with colour property or Bootstrap primary color for unknown types
 */
export function getFileTypeColour(fileName) {
  const extension = getFileExtension(fileName);
  if (!extension) return { color: '#19407b' }; // Bootstrap primary
  // Check configured file types 
  const config = fileTypeConfig[extension];
  return config ? { color: config.color } : { color: '#19407b' };
}

/**
 * Get user-friendly label for file type
 * @param {string} fileNameOrExtension - The name of the file or just the extension
 * @returns {string} User-friendly file type label
 */
export function getFileTypeLabel(fileNameOrExtension) {
  // Handle both full filename and just extension
  let extension;
  if (fileNameOrExtension && fileNameOrExtension.includes('.')) {
    extension = getFileExtension(fileNameOrExtension);
  } else {
    extension = fileNameOrExtension ? fileNameOrExtension.toLowerCase() : null;
  }
  if (!extension) return 'Files';
  
  // Check configured file types 
  const config = fileTypeConfig[extension];
  if (config) {
    return config.label;
  }
  
  // For unknown file types, create a generic but informative label e.g. "ABC Files"
  return `${extension.toUpperCase()} Files`;
}
